import { useEffect, useId, useState, type KeyboardEvent, type ReactNode } from 'react';
import { api } from '../../api';
import { Dialog } from '../../components/Dialog';
import { useToast } from '../../lib/toast';
import { isBilltoro } from '../../produkt';
import { fehlerText } from '../tarifHinweis';
import {
  gleichesWort,
  SCHLAGWOERTER_MAX,
  SCHLAGWORT_LAENGE,
  schlagwoerterUrl,
  type FirmaSchlagwort,
  type KontaktArt,
} from './KontaktDaten';
import './kontakte.css';

// Schlagwörter an Kunden und Lieferanten (eine Sammlung je Firma): Chips auf Karten und im Kopf, Feld im Formular,
// Filter in den Listen und das Fenster „Schlagwörter verwalten“ (umbenennen, entfernen für alle). Prüfen macht der
// Server (höchstens 10 je Kontakt, je 1–30 Zeichen, ohne Komma; die Schreibweise der Firma gewinnt).

const BILLTORO = isBilltoro();

// bis zu „zeige“ Chips, dann „+2“
export function SchlagwortChips({ woerter, zeige = 3 }: { woerter: string[]; zeige?: number }) {
  if (!woerter.length) return null;
  const rest = woerter.length - zeige;
  return (
    <span className="kt-chips" aria-label="Schlagwörter">
      {woerter.slice(0, zeige).map((w) => (
        <span key={w} className="kt-wort">
          {w}
        </span>
      ))}
      {rest > 0 && (
        <span className="kt-wort kt-wort-mehr" title={woerter.slice(zeige).join(', ')}>
          +{rest}
        </span>
      )}
    </span>
  );
}

// Text in Schlagwörter teilen (Komma trennt), Leerzeichen zusammenziehen
const teile = (text: string) =>
  text
    .split(',')
    .map((t) => t.replace(/\s+/g, ' ').trim())
    .filter(Boolean);

// Feld „Schlagwörter (optional)“: Chips mit „×“, Eingabe mit Vorschlägen der Firma; Enter oder Komma fügt hinzu,
// Verlassen des Felds auch (damit beim Speichern nichts Getipptes verloren geht)
export function SchlagwortFeld({ value, vorschlaege, onChange }: { value: string[]; vorschlaege: string[]; onChange(v: string[]): void }) {
  const [text, setText] = useState('');
  const [fehler, setFehler] = useState<string | null>(null);
  const id = useId();

  function hinzu(roh: string) {
    const neu = [...value];
    let f: string | null = null;
    for (const t of teile(roh)) {
      if (t.length > SCHLAGWORT_LAENGE) {
        f = `Ein Schlagwort hat höchstens ${SCHLAGWORT_LAENGE} Zeichen.`;
        continue;
      }
      if (neu.some((w) => gleichesWort(w, t))) continue;
      if (neu.length >= SCHLAGWOERTER_MAX) {
        f = `Höchstens ${SCHLAGWOERTER_MAX} Schlagwörter je Kontakt.`;
        break;
      }
      // Schreibweise der Firma, wenn es das Wort schon gibt
      neu.push(vorschlaege.find((v) => gleichesWort(v, t)) ?? t);
    }
    setFehler(f);
    setText('');
    if (neu.length !== value.length) onChange(neu);
  }

  function taste(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      hinzu(text);
    } else if (e.key === 'Backspace' && !text && value.length) {
      onChange(value.slice(0, -1));
    }
  }

  const frei = vorschlaege.filter((v) => !value.some((w) => gleichesWort(w, v)));
  return (
    <div className="field kt-feld">
      <label htmlFor={`${id}-in`}>Schlagwörter (optional)</label>
      <div className="kt-feld-box">
        {value.map((w) => (
          <span key={w} className="kt-wort">
            {w}
            <button
              type="button"
              className="kt-wort-x"
              aria-label={`Schlagwort „${w}“ entfernen`}
              onClick={() => onChange(value.filter((x) => x !== w))}
            >
              ×
            </button>
          </span>
        ))}
        {value.length < SCHLAGWOERTER_MAX && (
          <input
            id={`${id}-in`}
            value={text}
            list={`${id}-liste`}
            autoComplete="off"
            enterKeyHint="done"
            placeholder={value.length ? '' : 'z. B. Stammkunde'}
            onChange={(e) => {
              // Komma aus dem Einfügen oder vom Handy (dort kommt keine Taste „,“)
              if (e.target.value.includes(',')) hinzu(e.target.value);
              else setText(e.target.value);
            }}
            onKeyDown={taste}
            onBlur={() => text.trim() && hinzu(text)}
          />
        )}
        <datalist id={`${id}-liste`}>
          {frei.map((v) => (
            <option key={v} value={v} />
          ))}
        </datalist>
      </div>
      {fehler ? (
        <span className="err">{fehler}</span>
      ) : (
        <small>Zum Ordnen und Filtern, z. B. Stammkunde. Höchstens {SCHLAGWOERTER_MAX}.</small>
      )}
    </div>
  );
}

// Formular direkt geöffnet (ohne die Liste davor): die Liste einmal laden, damit das Feld „Schlagwörter“ die Wörter der
// Firma vorschlägt
export function MitVorschlaegen({ da, laden, children }: { da: boolean; laden(): Promise<void>; children: ReactNode }) {
  useEffect(() => {
    if (!da) void laden();
    // nur beim Öffnen
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return <>{children}</>;
}

// Auswahl „Alle Schlagwörter“ / je Schlagwort mit Anzahl in dieser Liste ('' = alle)
export function SchlagwortFilter({
  woerter,
  art,
  value,
  onChange,
}: {
  woerter: FirmaSchlagwort[];
  art: KontaktArt;
  value: string;
  onChange(v: string): void;
}) {
  const hier = woerter.filter((w) => (art === 'kunde' ? w.kunden : w.lieferanten) > 0);
  if (!hier.length && !value) return null;
  // gewählt in anderer Schreibweise (z. B. nach dem Zusammenführen): die der Firma
  const wahl = hier.find((w) => gleichesWort(w.wort, value))?.wort ?? value;
  return (
    <label className="kd-sort kt-filter">
      <span className="sr-only">Schlagwort</span>
      <select value={wahl} onChange={(e) => onChange(e.target.value)}>
        <option value="">Alle Schlagwörter</option>
        {hier.map((w) => (
          <option key={w.wort} value={w.wort}>
            {w.wort} ({art === 'kunde' ? w.kunden : w.lieferanten})
          </option>
        ))}
        {value && !hier.some((w) => gleichesWort(w.wort, value)) && <option value={value}>{value} (0)</option>}
      </select>
    </label>
  );
}

const anzahl = (n: number, eins: string, viele: string) => `${n} ${n === 1 ? eins : viele}`;
const beiWem = (w: FirmaSchlagwort) =>
  [
    w.kunden || !BILLTORO ? anzahl(w.kunden, 'Kunde', 'Kunden') : null,
    w.lieferanten ? anzahl(w.lieferanten, 'Lieferant', 'Lieferanten') : null,
  ]
    .filter(Boolean)
    .join(', ');

// Fenster „Schlagwörter verwalten“: je Zeile „Stammkunde · 12 Kunden, 1 Lieferant“ mit „Umbenennen“ und „Entfernen“
// (für alle Kunden und Lieferanten der Firma). Rückfrage zum Entfernen in derselben Zeile (ein Fenster zugleich).
// onErsetzt: damit der Filter der Liste dem umbenannten Wort folgt (entfernt: neu null)
export function SchlagwoerterDialog({
  open,
  firmId,
  woerter,
  onClose,
  onChanged,
  onErsetzt,
}: {
  open: boolean;
  firmId: string;
  woerter: FirmaSchlagwort[];
  onClose(): void;
  onChanged(): Promise<void>;
  onErsetzt?(alt: string, neu: string | null): void;
}) {
  const toast = useToast();
  // gerade in Arbeit: umbenennen (mit neuem Text) oder entfernen (Rückfrage)
  const [aktion, setAktion] = useState<{ wort: string; neu: string | null } | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function senden(alt: string, neu: string | null) {
    setBusy(true);
    setFehler(null);
    try {
      await api.put(schlagwoerterUrl(firmId), { alt, neu });
      onErsetzt?.(alt, neu === null ? null : (teile(neu)[0] ?? null));
      await onChanged();
      toast(neu === null ? 'Schlagwort entfernt' : 'Schlagwort umbenannt');
      setAktion(null);
    } catch (err) {
      setFehler(fehlerText(err));
    } finally {
      setBusy(false);
    }
  }

  const schliessen = () => {
    setAktion(null);
    setFehler(null);
    onClose();
  };
  return (
    <Dialog open={open} onClose={schliessen} title="Schlagwörter verwalten" className="kt-dlg">
      {woerter.length === 0 ? (
        <p className="muted">Noch keine Schlagwörter.</p>
      ) : (
        <ul className="kt-woerter">
          {woerter.map((w) => {
            const hier = aktion?.wort === w.wort ? aktion : null;
            return (
              <li key={w.wort}>
                {hier && hier.neu !== null ? (
                  <form
                    className="kt-umbenennen"
                    onSubmit={(e) => {
                      e.preventDefault();
                      void senden(w.wort, hier.neu ?? '');
                    }}
                  >
                    <label className="field">
                      <span>Neuer Name für „{w.wort}“</span>
                      <input
                        value={hier.neu}
                        maxLength={SCHLAGWORT_LAENGE}
                        autoFocus
                        onChange={(e) => setAktion({ wort: w.wort, neu: e.target.value })}
                      />
                    </label>
                    <div className="kt-zeile-act">
                      <button type="button" className="btn small" onClick={() => setAktion(null)}>
                        Abbrechen
                      </button>
                      <button type="submit" className="btn primary small" disabled={busy || !hier.neu.trim()}>
                        Umbenennen
                      </button>
                    </div>
                  </form>
                ) : hier ? (
                  <div className="kt-frage">
                    <span>
                      „{w.wort}“ bei {anzahl(w.kunden + w.lieferanten, 'Kontakt', 'Kontakten')} entfernen?
                    </span>
                    <div className="kt-zeile-act">
                      <button type="button" className="btn small" onClick={() => setAktion(null)}>
                        Abbrechen
                      </button>
                      <button type="button" className="btn dark small" disabled={busy} onClick={() => void senden(w.wort, null)}>
                        Ja, entfernen
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <span className="kt-wort-zeile">
                      <b>{w.wort}</b> <span className="muted">· {beiWem(w)}</span>
                    </span>
                    <span className="kt-zeile-act">
                      <button type="button" className="linkbtn" onClick={() => setAktion({ wort: w.wort, neu: w.wort })}>
                        Umbenennen
                      </button>
                      <button type="button" className="linkbtn rt-danger" onClick={() => setAktion({ wort: w.wort, neu: null })}>
                        Entfernen
                      </button>
                    </span>
                  </>
                )}
              </li>
            );
          })}
        </ul>
      )}
      <p className="rt-hint kt-klein">Umbenennen auf ein vorhandenes Schlagwort führt beide zusammen.</p>
      {fehler && <div className="notice bad">{fehler}</div>}
      <div className="dlg-actions">
        <button type="button" className="btn primary" onClick={schliessen}>
          Fertig
        </button>
      </div>
    </Dialog>
  );
}
